import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { FieldRenderProps } from 'react-final-form';
import { Button } from 'ncoded-component-library';
import XIcon from 'icons/X.icon';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { isEnterPressed } from 'utils';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './TeamsField.styles.scss';

export const MAX_NUM_OF_TEAMS = 12;

type TeamsFieldProps = FieldWrapperProps &
  FieldRenderProps<string[], HTMLElement>;

const TeamsField: React.FC<TeamsFieldProps> = (props) => {
  const {
    className,
    disabled,
    input: { value: formValue, onChange },
    ...rest
  } = props;

  const [value, setValue] = useState('');

  const inputRef = useRef<HTMLInputElement>();

  const { popError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const addTeam = useCallback(
    (value: string) => {
      if (formValue.length >= MAX_NUM_OF_TEAMS) {
        popError(t('maxNumberOfTeamsError'));
      } else if (!formValue?.some((val) => value === val)) {
        onChange([...formValue, value]);
      }
      setValue('');
    },
    [formValue, onChange, popError, t],
  );

  const handleOnKeyDown = useCallback(
    (e) => {
      if (isEnterPressed(e)) {
        e.preventDefault();
        value && addTeam(value);
      }
    },
    [addTeam, value],
  );

  const onRemove = useCallback(
    (team: string) => onChange(formValue.filter((val) => val !== team)),
    [formValue, onChange],
  );

  const onAddNew = useCallback(() => {
    if (value) {
      addTeam(value);
      return;
    }

    inputRef.current.focus();
  }, [addTeam, value]);

  const RenderTags = useMemo(
    () =>
      formValue.map((val) => (
        <div key={val} className="tag">
          <span>{val}</span>
          <Button
            disabled={disabled}
            variant="icon"
            icon={XIcon}
            onClick={() => onRemove(val)}
          />
        </div>
      )),
    [formValue, disabled, onRemove],
  );

  const classes = classNames('teams-field', className);

  return (
    <FieldWrapper className={classes} {...rest}>
      <div className="teams-textarea">
        <div className="teams-textarea__container">
          {!!formValue?.length && RenderTags}
          {formValue?.length < MAX_NUM_OF_TEAMS && (
            <Button
              className="add-new"
              variant="outline"
              iconPosition="right"
              icon={PluscircleIcon}
              disabled={disabled}
              onClick={onAddNew}
            >
              {t('addNew')}
            </Button>
          )}
        </div>
        <input
          ref={inputRef}
          value={value}
          disabled={disabled || formValue.length >= MAX_NUM_OF_TEAMS}
          onKeyDown={handleOnKeyDown}
          onChange={(e) => {
            setValue(e.target.value);
          }}
        />
      </div>
    </FieldWrapper>
  );
};

export default TeamsField;
